import S, { DataSignal } from 's-js';
import { SArray as ArraySignal } from 's-array';
import TZDate from 'lib/TZDate';

// TH 05/05/2017 -- A collection of small subcomponents related to grid-style reports.
// We've repeated bits of this functionality on several pages now, so this is an attempt at
// refactoring that into small reusable components.

/*
 * GridColumn -- Represents a column on a grid as a tight coupling of display logic and sort behavior.
 * RowSorter -- Encapsulates sorting behavior for GridColumns
 */
export interface GridColumn<TRow> {
    Title : string;
    DisplayValue : (row : TRow) => string;
    SortValue : (row : TRow) => number | string | TZDate;
    ClassName : string;
}

export function RowSorter<TRow>(
    initialColumn : GridColumn<TRow>,
    initialSortReversed : boolean = false
) : RowSorter<TRow> {
    const sortColumn = S.data(initialColumn),
        sortReversed = S.data(initialSortReversed);

    return {
        SortColumn: sortColumn,
        SortReversed: sortReversed,
        RowOrder,
        OnSortClick
    };

    function RowOrder(row1 : TRow, row2 : TRow) {
        const sortFn = sortColumn().SortValue,
            order = sortReversed() ? -1 : 1,
            r1 = sortFn(row1),
            r2 = sortFn(row2);

        return r1 < r2 ? -order : r1 > r2 ? order : 0;
    }

    function OnSortClick(col : GridColumn<TRow>) {
        S.freeze(() => S.sample(() => {
            if (sortColumn() === col) {
                sortReversed(!sortReversed());
            } else {
                sortColumn(col);
                sortReversed(false);
            }
        }));
    }
}

export interface RowSorter<TRow> {
    SortColumn : DataSignal<GridColumn<TRow>>;
    SortReversed : DataSignal<boolean>;
    RowOrder : (row1 : TRow, row2 : TRow) => number;
    OnSortClick : (col : GridColumn<TRow>) => void;
}
/* End GridColumn and RowSorter */


/* RowSelector -- Logic to support none/some/all selectors */

// RowSelectionStatus -- Selection state of visible rows.
export const enum RowSelectionStatus {
    None,
    Some,
    All
}

export function RowSelector<TRow>(
    rows : ArraySignal<TRow>,
    selectionProperty : (row : TRow) => DataSignal<boolean>
) {
    const allRowsSelected = rows.every(row => selectionProperty(row)()),
        someRowsSelected = rows.some(row => selectionProperty(row)()),
        rowSelectionStatus : DataSignal<RowSelectionStatus> = function (status? : RowSelectionStatus) {
            if (arguments.length === 0) {
                return allRowsSelected() ? RowSelectionStatus.All
                    : someRowsSelected() ? RowSelectionStatus.Some
                    : RowSelectionStatus.None;
            }
            S.freeze(() => S.sample(() => {
                if (status === RowSelectionStatus.None) {
                    rows().forEach(row => {
                        if (selectionProperty(row)()) selectionProperty(row)(false);
                    });
                } else if (status === RowSelectionStatus.All) {
                    rows().forEach(row => {
                        if (!selectionProperty(row)()) selectionProperty(row)(true);
                    });
                } else {
                    throw new Error('Invalid selection status ' + status + '. Only SelectionStatus.All or SelectionStatus.None allowed.');
                }
            }));
            return status!;
        };

    return {
        RowSelectionStatus: rowSelectionStatus
    };
}
export type RowSelector = ReturnType<typeof RowSelector>;
/* End RowSelector */


/* Row Expander -- Manages Expand All/Collapse All for grids w/ expandable rows */

// RowExpansionMode -- Whether new rows should AutoExpand or AutoCollapse.
export const enum RowExpansionMode {
    AutoCollapse = 0,
    AutoExpand = 1
}

export function RowExpander<TRow>(
    expansionMode : DataSignal<RowExpansionMode>,
    allRows : ArraySignal<TRow>,
    filteredRows : ArraySignal<TRow>,
    expansionProperty : (row : TRow) => DataSignal<boolean>
) {
    const allCurrentlyExpanded = filteredRows.every(r => expansionProperty(r)());

    S.on(expansionMode, () => allRows().forEach(r =>
        expansionProperty(r)(expansionMode() === RowExpansionMode.AutoExpand)), null, true);

    return {
        // TH 05/05/2017 -- Global expansion mode.
        // Calling MUST construct this as an S.data, not an S.value, and should use it as the
        // default if new rows are discovered.
        // Setting this will change the expansion status of every row.
        // Users can still expand or collapse individual rows w/o changing this value.
        ExpansionMode: expansionMode,
        // TH 05/05/2017 -- Current expansion status of visible rows.
        // This determines whether we show the Expand All or Collapse All link.
        // Note: it's important that ExpansionMode be an S.data and not an S.value.
        // Clicking Expand All a second time is not necessarily idempotent.
        AllCurrentlyExpanded: allCurrentlyExpanded
    };
}
export type RowExpander = ReturnType<typeof RowExpander>;
/* End RowExpander */



// WEBPACK FOOTER //
// ./apps/portcall/gridcomponents/model.ts